/**
 * /admin/users - cross-tenant user list with filters (read-only v1).
 *
 * Per CHAP-171 (Phase 1N #143). Admin-only. Invite + edit forms
 * deferred to v2 (Cabinet day-1 doesn't need self-service user
 * management - Jake handles via direct DB or admin tooling).
 *
 * Backed by GET /api/admin/users; filters apply server-side
 * (tenant/role/tier/emailStatus/q substring).
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api.js';

interface Membership {
  tenantSlug: string;
  tenantDisplayName: string;
  role: string;
  allowedRoles: string[];
  tier: string | null;
}

interface UserRow {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  emailStatus: string;
  lastLoginAt: string | null;
  createdAt: string;
  memberships: Membership[];
}

interface UsersResponse {
  users: UserRow[];
  count: number;
}

const EMAIL_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  valid: { label: 'Valid', cls: 'bg-green-100 text-green-800' },
  bounced: { label: 'Bounced', cls: 'bg-red-100 text-red-800' },
  spam_complaint: { label: 'Spam', cls: 'bg-amber-100 text-amber-900' },
};

const ROLE_BADGE: Record<string, string> = {
  customer: 'bg-blue-100 text-blue-800',
  team: 'bg-purple-100 text-purple-800',
  admin: 'bg-orange-100 text-orange-900',
};

export default function AdminUsers() {
  const [tenant, setTenant] = useState('');
  const [role, setRole] = useState('');
  const [tier, setTier] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [q, setQ] = useState('');

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (tenant) p.set('tenant', tenant);
    if (role) p.set('role', role);
    if (tier) p.set('tier', tier);
    if (emailStatus) p.set('emailStatus', emailStatus);
    if (q) p.set('q', q);
    p.set('limit', '200');
    return p.toString();
  }, [tenant, role, tier, emailStatus, q]);

  const { data, isLoading, error } = useQuery<UsersResponse, Error>({
    queryKey: ['admin-users', queryString],
    queryFn: () => apiGet<UsersResponse>(`/api/admin/users?${queryString}`),
  });

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Users</h2>
            <p className="text-neutral text-sm mt-1">
              Cross-tenant user list. Read-only v1 per CHAP-171; invite + edit
              forms deferred to v2.
            </p>
          </div>
        </div>
      </header>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-navy text-sm font-semibold mb-3">Filters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
          <Field label="Search (email or name)">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="jake@ or smith"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Tenant slug">
            <input
              type="text"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="omega-zeta"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Role">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">Any</option>
              <option value="customer">Customer</option>
              <option value="team">Team</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Tier">
            <input
              type="text"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              placeholder="cabinet / board / voter / observer"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Email status">
            <select
              value={emailStatus}
              onChange={(e) => setEmailStatus(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">Any</option>
              <option value="valid">Valid</option>
              <option value="bounced">Bounced</option>
              <option value="spam_complaint">Spam complaint</option>
            </select>
          </Field>
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading && (
          <div className="p-6 text-center text-neutral text-sm">Loading users…</div>
        )}
        {error && (
          <div className="border-l-4 border-red-400 bg-red-50 text-red-800 text-sm p-3">
            Failed to load: {error.message}
          </div>
        )}
        {data && (
          <>
            <div className="px-4 py-2 border-b border-gray-100 text-xs text-neutral">
              {data.count} user{data.count === 1 ? '' : 's'}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-neutral bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Tenants + Roles</th>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Last login</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-neutral">
                        No users match the filters.
                      </td>
                    </tr>
                  )}
                  {data.users.map((u) => {
                    const status = EMAIL_STATUS_BADGE[u.emailStatus] ?? {
                      label: u.emailStatus,
                      cls: 'bg-gray-100 text-gray-700',
                    };
                    return (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50 align-top">
                        <td className="px-3 py-2 min-w-0 max-w-[260px]">
                          <div className="font-medium text-navy truncate">
                            {u.name ?? '(unnamed)'}
                          </div>
                          <div className="text-neutral font-mono truncate" title={u.email}>
                            {u.email}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {u.memberships.length === 0 ? (
                            <span className="text-neutral italic">no tenants</span>
                          ) : (
                            <ul className="space-y-1">
                              {u.memberships.map((m) => (
                                <li
                                  key={m.tenantSlug}
                                  className="flex items-center gap-1.5 flex-wrap min-w-0"
                                >
                                  <span className="font-mono text-navy whitespace-nowrap">
                                    {m.tenantSlug}
                                  </span>
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                                      ROLE_BADGE[m.role] ?? 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {m.role}
                                  </span>
                                  {m.tier && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                                      {m.tier}
                                    </span>
                                  )}
                                  {m.allowedRoles && m.allowedRoles.length > 1 && (
                                    <span
                                      className="text-[10px] text-neutral italic"
                                      title={`Allowed roles: ${m.allowedRoles.join(', ')}`}
                                    >
                                      +{m.allowedRoles.length - 1} more
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${status.cls}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap text-neutral">
                          {u.lastLoginAt
                            ? new Date(u.lastLoginAt).toLocaleString()
                            : 'never'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block text-xs font-medium text-navy mb-1">{label}</span>
      {children}
    </label>
  );
}
